import { resourceListColumns } from '../resourceHelpers';
import { DetailsList, SelectionMode, DetailsListLayoutMode, makeStyles } from '@fluentui/react';
import { TimeSpinner } from '../../../../../_components/Loader/TimeSpinner';
import { useOnRender } from '../hooks/useOnRender';
import { useSearchUsers } from '../hooks/useSearchUsers';
import { useStore } from '../../../../../store/useStore';
import { useCallback, useState } from 'react';
import { TimeUser } from '../../../../../api/generated/data-contracts';
import { UpdateUserModal } from './UpdateUserModal';
import { FunctionOr } from '../../../../../types/FunctionOr';
import { unwrap } from '../../../../../helpers/unwrap';
import shallow from 'zustand/shallow';

export const ResourceList = () => {
    const classes = useStyles();
    const [selectedUser, setSelectedUser] = useState<TimeUser | null>(null);
    const { currentUsers, setCurrentUsers } = useStore(state => ({ currentUsers: state.currentUsers, setCurrentUsers: state.setCurrentUsers }), shallow);
    const setUser = useCallback(
        (update: FunctionOr<TimeUser | null>) => {
            const updatedUser = unwrap(update, selectedUser);
            setSelectedUser(updatedUser);
            setCurrentUsers(
                currentUsers.map(user => {
                    if (user.id === updatedUser.id) {
                        return updatedUser;
                    }
                    return user;
                }),
            );
        },
        [currentUsers, selectedUser, setCurrentUsers],
    );
    const { onRenderRow, onRenderItemColumn } = useOnRender({ classes, setUser, setSelectedUser });

    const { isFetching } = useSearchUsers();

    const handleItemInvoked = useCallback((item: TimeUser) => {
        setSelectedUser(item);
    }, []);

    const handleDismissModal = useCallback(() => {
        setSelectedUser(null);
    }, []);

    return (
        <>
            {isFetching ? (
                <TimeSpinner label="Searching for Users" />
            ) : (
                <>
                    <DetailsList
                        items={currentUsers}
                        columns={resourceListColumns}
                        onRenderRow={onRenderRow}
                        onRenderItemColumn={onRenderItemColumn}
                        onItemInvoked={handleItemInvoked}
                        selectionMode={SelectionMode.none}
                        layoutMode={DetailsListLayoutMode.justified}
                        usePageCache
                        useReducedRowRenderer
                        onShouldVirtualize={props => props.items.length > 50}
                        styles={{
                            root: {
                                position: 'static',
                                overflow: 'unset',
                                '.ms-DetailsRow-cell': { padding: 0, display: 'flex', alignItems: 'center' },
                                '.ms-DetailsHeader': {
                                    paddingTop: 4,
                                },
                                '.ms-DetailsRow': {
                                    cursor: 'pointer',
                                },
                            },
                            headerWrapper: {
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                            },
                        }}
                    />
                    {selectedUser && <UpdateUserModal isOpen onDismiss={handleDismissModal} user={selectedUser} setUser={setUser} />}
                </>
            )}
        </>
    );
};

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        height: '100%',
    },
    innerContainer: {
        position: 'relative',
        flexGrow: 1,
        paddingTop: 8,
    },
    selectedRow: {
        backgroundColor: theme.semanticColors.listItemBackgroundChecked,
        '&:hover': {
            backgroundColor: theme.semanticColors.listItemBackgroundCheckedHovered,
        },
    },
    disabledRow: {
        backgroundColor: 'rgba(0,0,0,0) !important',
        cursor: 'not-allowed',
        '& > *': {
            opacity: 0.5,
        },
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0) !important',
        },
    },
}));
